/**
 * FilmTitleCard — the opening slide of every tutorial film.
 *
 * A full-frame card in the platform's own colors with the tutorial's title
 * ("How to use the Library"), held for a moment and then faded out over the
 * first frame of the film. It is drawn by the player rather than baked into
 * the recording so the SAME silent film opens in English or in Hebrew, and
 * so a re-titled tutorial never needs a re-shoot.
 */

import { useEffect, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import './filmTitleCard.css';

const HOLD_MS = 1700;
const FADE_MS = 650;

interface Props {
  title: string;
  onDone: () => void;
}

export function FilmTitleCard({ title, onDone }: Props) {
  const { t } = useLanguage();
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const hold = setTimeout(() => setLeaving(true), HOLD_MS);
    const gone = setTimeout(onDone, HOLD_MS + FADE_MS);
    return () => {
      clearTimeout(hold);
      clearTimeout(gone);
    };
  }, [onDone]);

  return (
    <div
      className={`film-title-card ${leaving ? 'is-leaving' : ''}`}
      style={{ '--film-title-fade': `${FADE_MS}ms` } as React.CSSProperties}
      data-testid="film-title-card"
      aria-hidden
    >
      <span className="film-title-card-eyebrow">{t('learn.tutorial')}</span>
      <span className="film-title-card-title" dir="auto">{title}</span>
      <span className="film-title-card-rule" />
    </div>
  );
}
